import React, { useState, useEffect } from "react";
import { Input, Button, Modal, Form, Select, Row, Col } from "antd";
import { EditOutlined, LockOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store.ts";
import userService from "../../services/user-service.ts";
import Constant from "../../global/Constant.ts";
import { updatePassword } from "aws-amplify/auth";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";
import { setUserDetails } from "../../redux/userSlice.ts";
import { userTypeKey } from "../../global/enums.ts";
import "react-phone-input-2/lib/style.css";
import countries from "world-countries";
import { decryptData } from "../../utils/decryptData.ts"; // adjust path if needed
const { Option } = Select;

const MyAccount = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const [changepasswordform] = Form.useForm();
  const [userDetails, setUserDetailsState] = useState(null);
  const userData = useSelector((state: RootState) => state.auth.userData);
  const dispatch = useDispatch();

  // Helper function to check if user is Group or Individual
  const isGroupOrIndividual = () => {
    const userType = userTypeKey[userDetails?.UserType];
    return userType === "Group" || userType === "Individual";
  };

  // Helper function for expertise level
  const expertiseLevel = (level) => {
    const options = [
      { value: "1", label: "Embryologist" },
      { value: "2", label: "Clinician" },
      { value: "3", label: "Distributor" },
      { value: "4", label: "Professional" },
      { value: "5", label: "VITROMED Employee" },
    ];
    return options.find((opt) => opt.value === level)?.label || "Unknown";
  };

  // Preferred countries list
  const preferredCountries = ["DE", "GB", "US"];
  const sortedCountries = [
    ...countries.filter((c) => preferredCountries.includes(c.cca2)),
    ...countries.filter((c) => !preferredCountries.includes(c.cca2)),
  ];

  const filterOption = (input: string, option: { label?: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        dispatch(setLoading(true));
        const email = userData?.signInDetails?.loginId;
        if (email) {
          const responseEncrypted = await userService.getUserDetailsByEmail(
            Constant.userdetails
          );
          const response = await decryptData(
            responseEncrypted?.data,
            process.env.REACT_APP_ENCRYPTION_KEY!
          );

          if (!response.error) {
            setUserDetailsState(response);
          }
        }
      } catch (error) {
        console.log("Error fetching user details");
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchUserDetails();
  }, [userData, dispatch]);

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const showPasswordModal = () => setIsPasswordModalOpen(true);

  const handlePasswordModalOk = async () => {
    try {
      setConfirmLoading(true);
      const values = await changepasswordform.validateFields();

      if (values.newPassword !== values.confirmNewPassword) {
        return;
      }

      dispatch(setLoading(true));
      await updatePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      dispatch(
        addNotification({
          title: "Success",
          description: "Password changed successfully!",
          type: "success",
        })
      );

      setIsPasswordModalOpen(false);
      changepasswordform.resetFields();
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) return;

      let errorMessage = "An error occurred while changing the password.";
      if (error.name) {
        const errorMap = {
          NotAuthorizedException: "Current password is incorrect.",
          InvalidPasswordException:
            "New password doesn't meet complexity requirements.",
          LimitExceededException: "Too many attempts. Please try again later.",
          InvalidParameterException: "Invalid password parameters provided.",
        };
        errorMessage = errorMap[error.name] || error.message || errorMessage;
      }

      dispatch(
        addNotification({
          title: "Error",
          description: errorMessage,
          type: "error",
        })
      );
      console.error("Error changing password:", error);
    } finally {
      dispatch(setLoading(false));
      setConfirmLoading(false);
    }
  };

  const handlePasswordModalCancel = () => {
    setIsPasswordModalOpen(false);
    changepasswordform.resetFields();
  };

  const handleSubmit = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();
      const payload = {
        userId: userDetails.UserID,
        firstName: values.FirstName,
        lastName: values.LastName,
        phoneNumber: values.PhoneNumber,
        status: userDetails.Status,
        ExpertiseLevel: values.ExpertiseLevel,
        Country: values.Country,
      };

      if (userTypeKey[userDetails.UserType] === "Group") {
        payload.ClinicName = values.ClinicName;
      }

      dispatch(setLoading(true));
      const updateResponse = await userService.updateUserDetailsById(
        Constant.updateuser,
        payload
      );

      if (!updateResponse.error) {
        const updatedUserDetailsEncrypted =
          await userService.getUserDetailsByEmail(Constant.userdetails);
        const updatedUserDetails = await decryptData(
          updatedUserDetailsEncrypted?.data,
          process.env.REACT_APP_ENCRYPTION_KEY!
        );

        if (!updatedUserDetails.error) {
          setUserDetailsState(updatedUserDetails);
          dispatch(setUserDetails(updatedUserDetails));
          dispatch(
            addNotification({
              title: "Success",
              description: "Profile updated successfully!",
              type: "success",
            })
          );

          setIsModalOpen(false);
        }
      }
    } catch (error) {
      console.error("Failed to submit topic:", error);
      if (error.response?.data?.error) {
        dispatch(
          addNotification({
            title: "Error",
            description: error.response.data.error,
            type: "error",
          })
        );
      }
      console.log("Error updating user details");
    } finally {
      dispatch(setLoading(false));
      setConfirmLoading(false);
    }
  };

  return (
    <div className="vm-user-managemnt vm__container vm__h-100">
      <div className="vm__d-flex vm__flex-column vm__h-100">
        <div className="vm__flex-auto">
          <div className="vm__d-flex vm__align-items-center vm__mb-3 vm__px-3 vm__flex-wrap vm__gap-2">
            <div className="vm__flex-fill">
              <h3 className="vm__page-header">My Account</h3>
            </div>
            <div className="vm__flex-auto">
              <div className="vm__text-end vm__d-flex vm__gap-2">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={showModal}
                >
                  Edit Profile
                </Button>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  onClick={showPasswordModal}
                >
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="vm__flex-fill vm__overflow-auto">
          <div className="vm__px-3">
            <div className="vm__card-details">
              <Row gutter={[16, 16]}>
                <Col span={12} xs={24} sm={24}>
                  <div className="vm__info-container">
                    <h3>Name</h3>
                    <span>{`${userDetails?.FirstName || ""} ${
                      userDetails?.LastName || ""
                    }`}</span>
                  </div>
                </Col>
                <Col span={12} xs={24} sm={24}>
                  <div className="vm__info-container">
                    <h3>Email</h3>
                    <span>{userDetails?.Email || ""}</span>
                  </div>
                </Col>
                {isGroupOrIndividual() && (
                  <>
                    <Col span={12} xs={24} sm={24}>
                      <div className="vm__info-container">
                        <h3>Country</h3>
                        <span>{userDetails?.Country || ""}</span>
                      </div>
                    </Col>
                    <Col span={12} xs={24} sm={24}>
                      <div className="vm__info-container">
                        <h3>Phone</h3>
                        <span>{userDetails?.PhoneNumber || ""}</span>
                      </div>
                    </Col>
                  </>
                )}
                <Col span={12} xs={24} sm={24}>
                  <div className="vm__info-container">
                    <h3>User Type</h3>
                    <span>{userTypeKey[userDetails?.UserType]}</span>
                  </div>
                </Col>
                {isGroupOrIndividual() && (
                  <Col span={12} xs={24} sm={24}>
                    <div className="vm__info-container">
                      <h3>Expertise</h3>
                      <span>{expertiseLevel(userDetails?.ExpertiseLevel)}</span>
                    </div>
                  </Col>
                )}
                {userTypeKey[userDetails?.UserType] === "Group" && (
                  <Col span={12} xs={24} sm={24}>
                    <div className="vm__info-container">
                      <h3>Organization Name</h3>
                      <span>{userDetails?.ClinicName || ""}</span>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </div>
      <Modal
        centered
        title="Edit Profile"
        className="vm__modal"
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" initialValues={userDetails || {}}>
          <Form.Item
            label="First Name"
            name="FirstName"
            rules={[
              { required: true, message: "" },
              { whitespace: true, message: "" },
            ]}
            className="vm__mb-2"
          >
            <Input placeholder="Enter your first name" />
          </Form.Item>
          <Form.Item
            label="Last Name"
            name="LastName"
            rules={[
              { required: true, message: "" },
              { whitespace: true, message: "" },
            ]}
            className="vm__mb-2"
          >
            <Input placeholder="Enter your last name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="Email"
            rules={[{ required: true, message: "" }]}
            className="vm__mb-2"
          >
            <Input disabled placeholder="Enter your email" />
          </Form.Item>
          {isGroupOrIndividual() && (
            <>
              <Form.Item
                name="Country"
                label="Country"
                rules={[{ required: true, message: "" }]}
                className="vm__mb-2"
              >
                <Select
                  placeholder="Select a country"
                  showSearch
                  optionFilterProp="children"
                >
                  {sortedCountries.map((country) => (
                    <Option key={country.cca2} value={country.name.common}>
                      <img
                        src={`https://flagcdn.com/w40/${country.cca2.toLowerCase()}.png`}
                        alt={country.name.common}
                        style={{ width: 20, marginRight: 8 }}
                      />
                      {country.name.common}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Phone"
                name="PhoneNumber"
                rules={[
                  {
                    pattern: /^(\+?\d{7,16})$/,
                    message: "Enter a valid phone number",
                  },
                ]}
                className="vm__mb-2"
              >
                <Input
                  placeholder="Enter your phone number"
                  autoComplete="off"
                  maxLength={16}
                  onKeyPress={(e) => {
                    if (!/[\d+]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData("text");
                    if (!/^\+?\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            label="User Type"
            name="UserType"
            rules={[{ required: true, message: "" }]}
            className="vm__mb-2"
          >
            <Select
              showSearch
              placeholder="Select a user type"
              disabled
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[
                { value: 1, label: "Admin" },
                { value: 2, label: "Group" },
                { value: 0, label: "Individual" },
              ]}
            />
          </Form.Item>
          {isGroupOrIndividual() && (
            <Form.Item
              label="Expertise Level"
              name="ExpertiseLevel"
              rules={[{ required: true, message: "" }]}
              className="vm__mb-2"
            >
              <Select
                showSearch
                placeholder="Select Expertise"
                filterOption={filterOption}
                options={[
                  { value: "1", label: "Embryologist" },
                  { value: "2", label: "Clinician" },
                  { value: "3", label: "Distributor" },
                  { value: "4", label: "Professional" },
                  { value: "5", label: "VITROMED Employee" },
                ]}
              />
            </Form.Item>
          )}
          {userTypeKey[userDetails?.UserType] === "Group" && (
            <Form.Item
              label="Organization Name"
              name="ClinicName"
              rules={[{ required: true, message: "" }]}
              className="vm__mb-2"
            >
              <Input placeholder="Enter your organization name" />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal
        centered
        title="Change Password"
        className="vm__modal"
        open={isPasswordModalOpen}
        onOk={handlePasswordModalOk}
        onCancel={handlePasswordModalCancel}
        confirmLoading={confirmLoading}
        maskClosable={false}
      >
        <Form form={changepasswordform} layout="vertical">
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: "" }]}
            className="vm__mb-2"
          >
            <Input.Password placeholder="Enter your current password" />
          </Form.Item>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "" },
              {
                min: 8,
                message: "Password must be at least 8 characters long!",
              },
            ]}
            className="vm__mb-2"
          >
            <Input.Password placeholder="Enter your new password" />
          </Form.Item>
          <Form.Item
            label="Confirm New Password"
            name="confirmNewPassword"
            rules={[
              { required: true, message: "" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject("The two passwords do not match!");
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MyAccount;
